/**
 * The arena's scenery: two pixel gladiators trading blows on a 400×172
 * unsmoothed canvas, sixteen hand-drawn poses rather than one illustration
 * moved around.
 *
 * Ported from the original `combat.js` with the health bars removed. They
 * implied a contest with a winner, and this is a weighted lottery over every
 * token that paid the threshold — not a fight between two of them. The
 * animation now says nothing about the draw, which is the truth.
 */

type Step = [durationMs: number, wolfPose: number, bullPose: number, event?: string];

const exchange: Step[] = [
  [350, 0, 0], [200, 1, 1], [250, 0, 0],
  [170, 2, 1], [90, 3, 6, 'block'], [80, 3, 6], [130, 4, 6], [180, 5, 0],
  [260, 0, 1], [150, 1, 2], [80, 7, 3, 'wolfHit'], [80, 7, 3], [130, 7, 4], [180, 5, 5],
  [280, 0, 0], [180, 1, 0], [160, 2, 1], [80, 3, 7, 'bullHit'], [90, 3, 7], [140, 4, 7], [170, 5, 5],
  [340, 0, 0], [170, 1, 2], [80, 6, 3, 'block'], [80, 6, 3], [130, 6, 4], [180, 0, 5],
  [270, 0, 0], [170, 2, 1], [80, 3, 7, 'bullHit'], [70, 3, 7], [120, 4, 7], [160, 5, 5],
  [310, 0, 0], [160, 1, 2], [80, 7, 3, 'wolfHit'], [80, 7, 3], [140, 7, 4], [180, 5, 5], [400, 0, 0],
];

const total = exchange.reduce((n, x) => n + x[0], 0);

interface Pose {
  index: number;
  wolf: number;
  bull: number;
  event?: string;
}

function poseAt(time: number): Pose {
  let t = ((time % total) + total) % total;
  for (let i = 0; i < exchange.length; i++) {
    const step = exchange[i]!;
    if (t < step[0]) return {index: i, wolf: step[1], bull: step[2], ...(step[3] ? {event: step[3]} : {})};
    t -= step[0];
  }
  return {index: 0, wolf: 0, bull: 0};
}

interface Spark {
  x: number; y: number; vx: number; vy: number; life: number; initial: number; block: boolean;
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = src;
  });

export function mountCombat(canvas: HTMLCanvasElement | null, isPaused: () => boolean): () => void {
  if (!canvas) return () => {};
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};

  let dead = false;
  let raf = 0;
  let last = 0;
  let elapsed = 0;
  let previousIndex = -1;
  let sparks: Spark[] = [];
  let wolfX = 210;
  let bullX = 530;
  const frames: {wolf: HTMLImageElement[]; bull: HTMLImageElement[]} = {wolf: [], bull: []};

  const width = 1000;
  const height = 430;
  canvas.width = 400;
  canvas.height = 172;
  ctx.setTransform(0.4, 0, 0, 0.4, 0, 0);
  ctx.imageSmoothingEnabled = false;

  const draw = (time: number) => {
    if (dead) return;
    const dt = last ? Math.min(time - last, 50) : 0;
    last = time;
    const paused = isPaused() || document.hidden;
    if (!paused) elapsed += dt;
    const p = poseAt(elapsed);

    if (!paused) {
      const wolfTarget = [2, 3, 4].includes(p.wolf) ? 400 : 210;
      const bullTarget = [2, 3, 4].includes(p.bull) ? 350 : 530;
      const ease = Math.min(1, dt / 105);
      wolfX += (wolfTarget - wolfX) * ease;
      bullX += (bullTarget - bullX) * ease;
    }

    if (p.index !== previousIndex) {
      previousIndex = p.index;
      if (p.event) {
        const x = p.event === 'wolfHit' ? 380 : p.event === 'bullHit' ? 610 : p.wolf === 6 ? 385 : 600;
        for (let i = 0; i < 9; i++) {
          const angle = -2.7 + i * 0.55;
          const life = 240 + i * 12;
          sparks.push({
            x, y: 224,
            vx: Math.cos(angle) * (50 + i * 11),
            vy: Math.sin(angle) * (65 + i * 8),
            life, initial: life, block: p.event === 'block',
          });
        }
      }
    }

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#00000044';
    ctx.beginPath();
    ctx.ellipse(wolfX + 115, 324, 48, 6, 0, 0, Math.PI * 2);
    ctx.ellipse(bullX + 115, 324, 52, 6, 0, 0, Math.PI * 2);
    ctx.fill();

    const wolf = frames.wolf[p.wolf];
    const bull = frames.bull[p.bull];
    if (wolf) ctx.drawImage(wolf, Math.round(wolfX), 110, 230, 230);
    if (bull) ctx.drawImage(bull, Math.round(bullX), 110, 230, 230);

    for (const s of sparks) {
      if (!paused) {
        s.life -= dt;
        s.x += (s.vx * dt) / 1000;
        s.y += (s.vy * dt) / 1000;
        s.vy += (180 * dt) / 1000;
      }
      ctx.globalAlpha = Math.max(0, s.life / s.initial);
      ctx.fillStyle = s.block ? '#dfe6ef' : '#ffdca1';
      ctx.fillRect(Math.round(s.x), Math.round(s.y), 3, 3);
    }
    ctx.globalAlpha = 1;
    sparks = sparks.filter((s) => s.life > 0);

    raf = requestAnimationFrame(draw);
  };

  Promise.all(
    (['wolf', 'bull'] as const).flatMap((name) =>
      Array.from({length: 8}, (_, i) =>
        loadImage(`/assets/combat/${name}-${i}.webp`).then((im) => {
          frames[name][i] = im;
        }),
      ),
    ),
  )
    .then(() => {
      if (!dead) raf = requestAnimationFrame(draw);
    })
    .catch(() => {
      // Scenery failing to load is not worth an error state on the page.
    });

  return () => {
    dead = true;
    cancelAnimationFrame(raf);
  };
}
